import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { Pill } from '../../../components/pill/Pill';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { getInvoices } from '../models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { InvoiceStatusToColor, InvoiceStatusToText } from '../utils/status';
import { ExportInvoicesButton } from './ExportSalesButton';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';

type InvoiceSortBy = 'date' | 'expiresAt' | 'status' | 'totalInclVat' | 'invoiceNumber';

const INVOICES_HEADERS: ITableHeader[] = [
  {
    id: 'invoiceNumber',
    name: 'Factuur Nummer',
    sortable: true,
  },
  {
    id: 'customerRef',
    name: 'Klant referentie',
  },
  {
    id: 'date',
    name: 'Factuur Datum',
    sortable: true,
  },
  {
    id: 'expiresAt',
    name: 'Vervaldatum',
    sortable: true,
  },
  {
    id: 'customer',
    name: 'Klant',
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
  },
  {
    id: 'totalInclVat',
    name: 'Totaal',
    sortable: true,
  },
];

export const SalesInvoicesPage = () => {
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');
  const [sortBy, setSortBy] = useState<InvoiceSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getInvoices(
        variables.cursor,
        variables.take,
        {
          search: searchValue,
        },
        sortBy,
        sortOrder,
      );
    },
    [searchValue, sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  useEffect(() => {
    page.reset();
  }, [searchValue, sortBy, sortOrder]);

  const title = 'Verkopen';

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <>
                <ExportInvoicesButton />
                <LinkButton to="new-manual" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe manuele verkoop
                </LinkButton>
                <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                  Nieuwe verkoop
                </LinkButton>
              </>
            }
          />
        }
        panel
        filters={
          <Input
            type="text"
            placeholder="Zoek een verkoop..."
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={INVOICES_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as InvoiceSortBy | undefined);
            setSortOrder(order);
          }}
          mapData={(invoice) => {
            return [
              <Link to={`${invoice.id}`} className="link-text">
                {invoice.invoiceNumber ?? 'DRAFT'}
              </Link>,
              invoice.customerRef ?? '-',
              formatDate(invoice.date),
              invoice.expiresAt ? formatDate(invoice.expiresAt) : '-',
              invoice.customer.name,
              <Pill color={InvoiceStatusToColor[invoice.status]} size="sm">
                {InvoiceStatusToText[invoice.status]}
              </Pill>,
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(invoice.totalInclVat, 2, {
                decimalSeperator: ',',
              })}`}</div>,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
